import { useTranslation } from "react-i18next";
import { ChartCard, type Column, ColumnChart, HBarList, holatVar, Legend } from "@/shared/charts";
import type { AnalyticsLayoutProps } from "../../types";
import { KpiRow, sonMatni } from "./KpiRow";

/** Top ro'yxatlar uzunligi — karta balandligi cheklangan. */
const TOP_N = 8;

/**
 * Umumiy analitika ko'rinishi — registrda o'z layout'i YO'Q kind uchun
 * (`analyticsLayouts.ts`): KPI qatori + kunlik ustunlar + top xodimlar +
 * bo'limlar.
 *
 * ⚠️ `primary`/`flagged` ma'nosi kind'ga bog'liq (`zone_exits` da
 * `flagged` — `primary` ning qismi, `attendance_daily` da esa alohida
 * holat), shuning uchun bu yerda ular QATLAM EMAS, YONMA-YON ikki
 * bo'lak sifatida neytral yorliqlar bilan chiziladi — kind'ga xos
 * ma'no o'z layout'ida beriladi. Z1 dan keyin bu komponent hech bir
 * kind uchun ishlatilmasligi kutiladi.
 */
export function GenericAnalytics({ kind, data }: AnalyticsLayoutProps) {
	const { t } = useTranslation("reports");
	const asosiy = t("analytics.generic.primary");
	const belgilangan = t("analytics.generic.flagged");
	const bosh = t("analytics.generic.empty");

	const ustunlar: Column[] = data.daily.map((kun) => ({
		key: kun.date,
		label: kun.date.slice(8, 10),
		stack: [
			{ value: kun.primary, color: holatVar("kelgan"), label: asosiy },
			{ value: kun.flagged, color: holatVar("kelmagan"), label: belgilangan },
		],
		tooltip: `${kun.date}: ${asosiy} ${sonMatni(kun.primary)} · ${belgilangan} ${sonMatni(kun.flagged)}`,
	}));

	return (
		<div data-testid="generic-analytics" className="flex flex-col gap-3.5">
			<KpiRow kind={kind} totals={data.totals} spark={data.spark} />

			<div className="grid gap-3.5 lg:grid-cols-[1.4fr_1fr]">
				<ChartCard
					title={t("analytics.generic.dailyTitle")}
					extra={
						<Legend
							items={[
								{ label: asosiy, color: holatVar("kelgan") },
								{ label: belgilangan, color: holatVar("kelmagan") },
							]}
						/>
					}
				>
					<ColumnChart columns={ustunlar} empty={bosh} />
				</ChartCard>

				<ChartCard title={t("analytics.generic.topEmployeesTitle")}>
					<HBarList
						empty={bosh}
						rows={data.topEmployees.slice(0, TOP_N).map((xodim) => ({
							key: xodim.employeeId,
							label: xodim.employeeName,
							value: xodim.total,
							valueText: sonMatni(xodim.total),
						}))}
					/>
				</ChartCard>
			</div>

			<ChartCard title={t("analytics.generic.byDepartmentTitle")}>
				<HBarList
					empty={bosh}
					rows={data.byDepartment.slice(0, TOP_N).map((bolim) => ({
						key: bolim.departmentId ?? "__none",
						label: bolim.departmentName ?? t("analytics.generic.noDepartment"),
						value: bolim.total,
						valueText: sonMatni(bolim.total),
					}))}
				/>
			</ChartCard>
		</div>
	);
}
